import * as React from 'react';
import * as classnames from 'classnames';
import PortalMainContainer from '../../../common/portal/PortalMainContainer';
import { interpolate } from '../../../../utils/string';
import { getConfirmationBox } from '../../../common/swal/Sweetalert';
import { alertMessage, titleHeader } from '../../../../constants/alertMessage';
import NotifyHOC from '../../../../HOC/Notifications';
import {
    PanelType,
    PriceType,
    Project,
    PROJECT_EDIT_STATUS,
    TENANT_LEVEL
} from '@nexxt/common/types';

import { PROJECT_STATUS } from '@nexxt/common/types';
import {
    CHECKOUT,
    LAUNCH_PROJECT,
    PREVIEW
} from '../../../../constants/routes';
import CircleButton from '../../../common/CircleButton';
import config from '../../../../config';
import { PROJECT_EDIT_MODES } from '../../../../interface/project.interface';
import { ILocation } from '../../../../interface/common.interface';
import { getTenantLevel } from '@nexxt/common/firebase/auth/userDetails';
import ValidationService from '@nexxt/common/services/ValidationService';
import ProjectDropdown from '../../../projects/ProjectDropdown';
import { sendEvent } from '../../../../services/utils.service';
import {
    SVC_EVENT_NAME,
    SVC_EVENT_TYPE,
    SVC_NAMESPACE_TYPE
} from '@nexxt/common/constants';
import * as projectService from '../../../../services/project.service';

export interface IProjectLaunchProps {
    // props
    location?: any;
    status?: string;
    devMode?: boolean;
    isLaunching?: boolean;
    isEdited?: boolean;
    projectId?: number;
    toastNotification: any;
    currentProject: Project;
    codeName: string;
    packageID: number;
    tenantURL: string;
    tenantId: string;
    projectHealth: any;
    priceType: any;
    editMode: PROJECT_EDIT_MODES;
    editStatus: PROJECT_EDIT_STATUS;
    totalQuestionCount: number;
    // props methods
    onLaunch?: (
        projectId: number,
        packageID: number,
        codeName: string
    ) => Promise<any>;
    onPause?: (projectId: number, status: PROJECT_STATUS) => Promise<any>;
    changePath: (path: ILocation | string) => void;
    onOpenPriceModal: () => void;
    showErrorPopup: () => void;
    showWarningPopup: () => void;
    showErrorPopupWindow: () => void;
    showWarningPopupWindow: () => void;
    showPricingPopup: () => void;
    showProjectLockPopup: () => void;
    launchSandbox: () => void;
}

interface IProjectLaunchStates {}
class ProjectLaunch extends React.Component<
    IProjectLaunchProps,
    IProjectLaunchStates
> {
    editStatusSwitch = () => {
        switch (this.props.editStatus) {
            case PROJECT_EDIT_STATUS.EDIT_REQUIRED:
                return 'Fields require attention';
            case PROJECT_EDIT_STATUS.EDITING:
                return 'Editing...';
            case PROJECT_EDIT_STATUS.EDITED:
                return 'Saving...';
            case PROJECT_EDIT_STATUS.FAILED:
                return 'Something went wrong.. ';
            default:
                return 'All changes saved';
        }
    };

    // *TODO: Validate project setting before publish.
    isValid = () => {
        let isError = false;
        const { currentProject } = this.props;
        const validationService = new ValidationService();
        const { isEmpty, validateGreetingTexts, validateRedirectMessage } =
            validationService;
        const errorObject: any = {
            title: '',
            redirectSettings: '',
            greetingLabel: '',
            greetingTexts: ''
        };

        if (isEmpty(currentProject.title)) {
            isError = true;
            errorObject.title = 'Project title(Bot settings),\n';
        }

        if (
            validateGreetingTexts(
                currentProject.greetingText,
                currentProject.languages
            ).isError
        ) {
            isError = true;
            errorObject.greetingTexts = 'Greeting text(Greeting question),\n';
        }
        if (isEmpty(currentProject.greetingLabel)) {
            isError = true;
            errorObject.greetingLabel = 'Greeting label(Greeting question),\n';
        }

        if (
            validateRedirectMessage(
                currentProject.redirectSettings,
                currentProject.languages
            ).isError
        ) {
            isError = true;
            errorObject.redirectSettings = 'Redirect message question, \n';
        }
        return {
            isError,
            errorObject
        };
    };
    onPause = async () => {
        try {
            getConfirmationBox({
                title: titleHeader.PAUSE_PROJECT,
                message: alertMessage.CONFIRM_PAUSE,
                subMessage: alertMessage.PAUSE_ALERT,
                showCloseButton: true,
                confirmButtonColor: '#5EBC62',
                iconClass: 'info'
            }).then(async (x) => {
                if (x.dismiss) {
                    this.props.toastNotification.error(
                        `User cancel the process. Please try again!`
                    );
                } else {
                    const response = await this.props.onPause(
                        this.props.projectId,
                        PROJECT_STATUS.PAUSED
                    );
                    if (response.status === 200) {
                        this.props.toastNotification.success(response.message);
                    }
                }
            });
        } catch (err) {
            console.error(err);
            this.props.toastNotification.error(alertMessage.UNABLE_PAUSE);
        }
    };
    onLaunch = async () => {
        const tenantLevel = getTenantLevel();
        const isRepublish = this.props.status !== PROJECT_STATUS.DRAFT;
        const checkValididity = this.isValid();
        const errorsNumber = Object.values(this.props.projectHealth).filter(
            (problem) => problem['type'] === 'ERROR'
        ).length;

        const warningsNumber = Object.values(this.props.projectHealth).filter(
            (problem) => problem['type'] === 'WARNING'
        ).length;

        if (checkValididity.isError) {
            this.props.toastNotification.warning(
                `
                ${alertMessage.UNABLE_LAUNCH_ERROR}
                ${checkValididity.errorObject.title} 
                ${checkValididity.errorObject.greetingTexts}
                ${checkValididity.errorObject.greetingLabel}
                ${checkValididity.errorObject.redirectSettings}
                `
            );
        }

        if (errorsNumber > 0) {
            this.props.showErrorPopupWindow();
            return;
        }

        if (warningsNumber > 0) {
            this.props.showWarningPopupWindow();
            return;
        }

        if (tenantLevel === TENANT_LEVEL.SANDBOX) {
            this.props.launchSandbox();
            return;
        }
        if (
            ![PriceType.REGULAR, PriceType.FREE].includes(
                this.props.priceType
            ) &&
            !isRepublish
        ) {
            this.props.showPricingPopup();
            return;
        }

        try {
            getConfirmationBox({
                title: titleHeader.PUBLISH_LAUNCH,
                message: isRepublish
                    ? alertMessage.CONFIRM_REPUBLISH
                    : alertMessage.CONFIRM_PUBLISH,
                subMessage: isRepublish
                    ? alertMessage.REPUBLISH_ALTERT
                    : alertMessage.PUBLISH_ALERT,
                showCloseButton: true,
                confirmButtonColor: '#5EBC62',
                iconClass: 'info'
            }).then(async (x) => {
                if (!x.dismiss) {
                    if (this.props.packageID) {
                        const response = await this.props.onLaunch(
                            this.props.projectId,
                            this.props.packageID,
                            this.props.codeName
                        );
                        if (response.status === 200) {
                            this.props.changePath(
                                interpolate(LAUNCH_PROJECT, {
                                    projectId: this.props.currentProject.id
                                })
                            );
                        }
                        return;
                    }
                    this.props.changePath({
                        pathname: interpolate(CHECKOUT, {
                            projectId: this.props.currentProject.id
                        }),
                        search: this.props.devMode ? '?mode=dev' : ''
                    } as ILocation);
                }
            });
        } catch (error) {
            console.error(error);
            this.props.toastNotification.error(alertMessage.UNABLE_LAUNCH);
        }
    };
    redirectToPreview = () => {
        this.props.changePath({
            pathname: interpolate(PREVIEW, {
                projectId: this.props.currentProject.id
            })
        } as ILocation);
    };
    render() {
        const { status, codeName, isEdited, editMode, priceType, location } =
            this.props;
        const { pathname } = location;
        const onQuestionTab = pathname?.includes('/questions');
        const onPreviewTab = pathname?.includes('/preview');
        const CHATBOT_URL =
            this.props.tenantURL + '.' + config.host.domain + '/p/' + codeName;

        const errorsNumber = Object.values(this.props.projectHealth).filter(
            (problem) => problem['type'] === 'ERROR'
        ).length;

        const warningsNumber = Object.values(this.props.projectHealth).filter(
            (problem) => problem['type'] === 'WARNING'
        ).length;
        const isDisabledMode = editMode === PROJECT_EDIT_MODES.DISABLED;
        const isFeasible = priceType?.PRICE_MODE !== PriceType.NOT_FEASIBLE;
        const isOutdatedPreview =
            new Date(this.props.currentProject.lastModifiedOn).getTime() >
            new Date(this.props.currentProject.previewDate).getTime();

        return (
            <PortalMainContainer>
                <React.Fragment>
                    <span className="actions">
                        {/* {onQuestionTab && ( */}
                        <span
                            className={classnames('actions__status', {
                                error: [
                                    PROJECT_EDIT_STATUS.EDIT_REQUIRED,
                                    PROJECT_EDIT_STATUS.FAILED
                                ].includes(this.props.editStatus)
                            })}
                        >
                            <i>{this.editStatusSwitch()}</i>
                        </span>
                        {/* )} */}
                        {warningsNumber > 0 && (
                            <CircleButton
                                value={warningsNumber}
                                type="warn"
                                onClick={this.props.showWarningPopup}
                            />
                        )}
                        {errorsNumber > 0 && (
                            <CircleButton
                                value={errorsNumber}
                                type="err"
                                onClick={this.props.showErrorPopup}
                            />
                        )}
                        {isDisabledMode && status !== PROJECT_STATUS.REVIEW && (
                            <button
                                type="button"
                                className={classnames(
                                    'btn btn--md btn--orange mr-2'
                                )}
                                title="Click to disable read-only mode"
                                onClick={location.reload}
                            >
                                <span
                                    onClick={this.props.showProjectLockPopup}
                                    style={{ marginLeft: '5px' }}
                                >
                                    Disable Read-Only Mode
                                </span>
                            </button>
                        )}
                        {this.props.currentProject?.status !==
                            PROJECT_STATUS.LIVE &&
                        this.props.currentProject?.panelSettings?.panelType ==
                            PanelType.INCA ? (
                            <button
                                type="button"
                                className={classnames('btn btn--md mr-2', {
                                    'btn--orange': !isFeasible,
                                    'btn--green': isFeasible
                                })}
                                data-test="incidence-rate-btn"
                                disabled={isDisabledMode}
                                title="Click to see price details"
                                onClick={() => {
                                    this.props.onOpenPriceModal();
                                }}
                            >
                                {<i className="fas fa-tag"></i>}
                                <span style={{ marginLeft: '5px' }}>
                                    {this.props.priceType?.label ||
                                        'Calculating...'}
                                </span>
                            </button>
                        ) : null}
                        <button
                            type="button"
                            className={`btn btn--md ${
                                !onPreviewTab && isOutdatedPreview
                                    ? 'btn--red'
                                    : 'btn--green'
                            }`}
                            onClick={this.redirectToPreview}
                            data-tip="custom show"
                            data-for="preview button"
                            data-test="proceed-to-preview-main"
                            title="Click to go to preview"
                        >
                            {onPreviewTab
                                ? 'Preview has been updated'
                                : isOutdatedPreview
                                ? 'Click here to update your Preview'
                                : 'Preview Study'}
                        </button>
                        {/* <button
                            type="button"
                            className="btn btn--md btn--green ml-2"
                            onClick={this.onLaunch}
                            data-tip="custom show"
                            data-for="launch button"
                            data-test="proceed-to-checkout-main"
                            disabled={
                                isDisabledMode || !this.props.totalQuestionCount
                            }
                        >
                            {tenantLevel === TENANT_LEVEL.SANDBOX
                                ? isSandboxMode
                                    ? 'Request Review'
                                    : 'In-Review'
                                : status && status !== PROJECT_STATUS.DRAFT
                                ? 'Republish'
                                : 'Proceed to Checkout'}
                        </button> */}
                        <ProjectDropdown
                            isEditor={true}
                            project={this.props.currentProject}
                            tenantId={this.props.tenantId}
                            isGlobal={false}
                        />
                    </span>
                </React.Fragment>
            </PortalMainContainer>
        );
    }
}

export default NotifyHOC(ProjectLaunch);
